import { AfterViewChecked, ChangeDetectionStrategy, Component, computed, ElementRef, inject, OnDestroy, signal, viewChild } from '@angular/core';
import { OnboardingTourService } from './onboarding-tour.service';
import { I18n, NgtButtonComponent, TranslatePipe } from '@corporate/ng-tebis-base-components';

@Component({
  selector: 'tebitasks-onboarding-tour',
  imports: [
    NgtButtonComponent,
    TranslatePipe,
  ],
  templateUrl: './onboarding-tour.component.html',
  styleUrl: './onboarding-tour.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class OnboardingTourComponent implements AfterViewChecked, OnDestroy {

  tour = inject(OnboardingTourService);
  messageBox = viewChild<ElementRef<HTMLElement>>('messageBox');

  messageBoxTop = signal(0);
  messageBoxLeft = signal(0);

  position = computed(() => {
    if (this.tour.currentMessage()) {
      return this.tour.currentMessage()!.position || null;
    }
    return null;
  });

  isLastMessage = computed(() => {
    return this.tour.currentMessageIndex() === this.tour.messages().length - 1;
  });

  hasBackButton = computed(() => {
    return this.tour.currentMessageIndex() > 0;
  });

  constructor() {
    inject(I18n).addNamespace('help');
    this.onReferredElementClicked = this.onReferredElementClicked.bind(this);
  }

  ngAfterViewChecked(): void {
    const message = this.tour.currentMessage();
    const messageBox = this.messageBox();
    if (messageBox && message?.query) {
      const referredElements = document.querySelectorAll(message!.query!);
      if (referredElements.length > 0) {
        this.setReferredElement(referredElements[0]);
        this.positionMessageBox(referredElements[0]);
        return;
      }
    }
    this.setReferredElement(null);
    this.positionMessageBox(null);
  }

  ngOnDestroy(): void {
    this.setReferredElement(null);
  }

  referredElement = signal<Element|null>(null);
  setReferredElement(element: Element|null) {
    if (this.referredElement()) {
      this.referredElement()!.removeEventListener('click', this.onReferredElementClicked);
    }
    this.referredElement.set(element);
    if (this.referredElement()) {
      this.referredElement()!.addEventListener('click', this.onReferredElementClicked);
    }
  }

  onReferredElementClicked() {
    if (this.tour.currentMessage()?.skipWhenQuerriedClicked) {
      this.tour.next();
    }
  }

  positionMessageBox(refElement: Element|null) {
    const currentMessage = this.tour.currentMessage();
    if (!currentMessage) { return; }
    if (!this.messageBox()) { return; }
    const messageBoxRect = this.messageBox()!.nativeElement.getBoundingClientRect();
    if (refElement === null || !currentMessage.query || !currentMessage.position) {
      const rect = document.body.getBoundingClientRect();
      this.messageBoxTop.set(rect.height / 2 - messageBoxRect.height / 2);
      this.messageBoxLeft.set(rect.width / 2 - messageBoxRect.width / 2);
      return;
    }
    const rect = refElement.getBoundingClientRect();
    let top = 0;
    let left = 0;
    switch (currentMessage.position) {
      case 'above': top = rect.top - messageBoxRect.height - 24; break;
      case 'below': top = rect.bottom + 24; break;
      case 'left': left = rect.left - messageBoxRect.width - 24; break;
      case 'right': left = rect.right + 24; break;
      default: break;
    }
    if (currentMessage.position === 'above' || currentMessage.position === 'below') {
      left = rect.left + rect.width / 2 - messageBoxRect.width / 2;
    }
    if (currentMessage.position === 'left' || currentMessage.position === 'right') {
      top = rect.top + rect.height / 2 - messageBoxRect.height / 2;
    }
    this.messageBoxTop.set(top);
    this.messageBoxLeft.set(left);
  }
}
